import { ExternalLink, Home, MessageCircle, Truck } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useOrder } from "../api/hooks";
import { NeedTelegram } from "../components/NeedTelegram";
import { OrderContact, OrderLines, OrderProgress, StatusBadge } from "../components/OrderParts";
import { formatDate, formatOrderNumber } from "../lib/money";
import { useSupport } from "../lib/support";
import { hasTelegramContext, openExternalLink } from "../lib/telegram";
import { useCanGoBack } from "../lib/use-can-go-back";

const STATUS_NOTE = {
  new: "Заявка получена. Менеджер свяжется с вами для подтверждения заказа и доставки.",
  confirmed: "Заказ подтверждён. Готовим отправку — трек-номер появится здесь.",
  shipped: "Заказ в пути.",
  delivered: "Заказ доставлен. Спасибо, что выбрали BioFlow!",
  cancelled: "Заказ отменён. Если это ошибка — напишите нам.",
} as const;

/**
 * Экран заказа: статус по шкале «Оформлен → Подтверждён → Отправлен →
 * Доставлен», состав, контакты и трек-номер. Пока заказ в пути, хук
 * опрашивает API — статус меняет менеджер со своей стороны.
 */
export function OrderScreen() {
  const { orderNumber = "" } = useParams();
  const navigate = useNavigate();
  const order = useOrder(orderNumber);
  const support = useSupport();
  const canGoBack = useCanGoBack();

  if (!hasTelegramContext()) return <NeedTelegram what="Статус заказа" />;
  if (order.isPending) return <div className="state">Загружаем…</div>;
  if (order.isError || !order.data) return <div className="state">Заказ не найден</div>;

  const data = order.data;

  return (
    <div className="screen screen--plain">
      <div className="screen__header">
        <h1 className="screen__title">Заказ {formatOrderNumber(data.orderNumber)}</h1>
        <StatusBadge status={data.status} />
      </div>
      <p className="screen__subtitle">Оформлен {formatDate(data.placedAt)}</p>

      <OrderProgress status={data.status} />
      <p className="small muted" style={{ margin: "12px 0 16px" }}>{STATUS_NOTE[data.status]}</p>

      <OrderLines order={data} />

      <div className="section">Контакты и доставка</div>
      <OrderContact order={data} />

      {(data.status === "shipped" || data.status === "delivered") && (
        <>
          <div className="section">
            <Truck size={16} strokeWidth={1.9} aria-hidden /> Отправление
          </div>
          <div className="small">
            {data.shipment.carrier && <div>{data.shipment.carrier}</div>}
            {data.shipment.trackingNumber && <div>Трек-номер: <strong>{data.shipment.trackingNumber}</strong></div>}
            {data.shipment.shippedAt && <div className="muted">Отправлен {formatDate(data.shipment.shippedAt)}</div>}
            {data.shipment.deliveredAt && <div className="muted">Доставлен {formatDate(data.shipment.deliveredAt)}</div>}
            {data.shipment.trackingUrl && (
              <button type="button" className="button button--ghost" style={{ marginTop: 10 }} onClick={() => openExternalLink(data.shipment.trackingUrl!)}>
                <ExternalLink size={16} strokeWidth={1.9} aria-hidden /> Отследить посылку
              </button>
            )}
          </div>
        </>
      )}

      <div className="stack" style={{ marginTop: 20 }}>
        <button type="button" className="button button--ghost" onClick={support.open} disabled={!support.available}>
          <MessageCircle size={17} strokeWidth={1.9} aria-hidden /> Задать вопрос
        </button>
        {/* Вход по прямой ссылке из уведомления: истории нет, кнопки «назад» нет. */}
        {!canGoBack && (
          <button type="button" className="button button--ghost" onClick={() => navigate("/")}>
            <Home size={17} strokeWidth={1.9} aria-hidden /> На главную
          </button>
        )}
      </div>
    </div>
  );
}
